/**
 * **전장 특성.** 깊이마다 그 판만의 성격을 한두 장 붙인다 — 적 편성이 궁수 위주이거나, 숲이 우거졌거나.
 *
 * 플레이한 사람이 청했다(Todo5): "어떤 전장은 … 궁수가 많거나, 기병이 많은 등 병종 적인 특색이 있을 수 있겠네.
 * 플레이어가 연속으로 여러뎁스를 격파하려면 머리를 쓰게하는게 목적이야. 초반에는 너무 어려울 필요없고, 중 후반으로
 * 갈수록 어려워지면 되겠어." 설계 전체는 노션 Todo5와 `docs/design.md`에 있다. 1단계는 여섯 특성, 2단계는 땅의
 * 크기와 제약(넓은 평원·여울·적의 고지·산길·좁은 협곡), 3단계는 전황(바람·비·매복·원군)이다. 5단계는 관문 이야기 판,
 * 6단계는 갈림길(`Route`) — 관문을 넘으면 다음 네 깊이의 땅을 고른다.
 *
 * 출정 중에는 편성을 바꿀 수 없다(결과 화면에서 철수·진군만 고른다). 그래서 특성은 **미리 보여야** 한다 — 못 보고
 * 당하면 퍼즐이 아니라 운이다. 출정 시드(`RunState.seed`)와 깊이로 정해져, 결과 화면이 다음 두 깊이를 예보한다.
 *
 * **전투 판정에 들어가는 값이다**(적 편성·지형·방어·전과). 리플레이의 명부(`ScenarioConfig.traits`)에 실리고,
 * 문 앞에서 거르며(`sanitizeTraits`), 검증(`verifyReplay`)이 출정 시드·깊이에서 다시 낸 값(`traitsOfBattle`)과 맞춰
 * 본다. 특성을 더하거나 값을 바꾸면 `REPLAY_VERSION`을 올린다 — `TRAITS`의 순서·`minDepth`·`traitsForDepth`의 곡선을
 * 바꾸는 것도 그렇다(같은 출정이 다른 특성을 내면 정직한 리플레이가 거절된다).
 */
import { Rng } from './rng.ts';

export const Trait = {
  /** 적 궁수 두 배. 답: 방패 든 창, 기병 우회. */
  ArcherCorps: 'archers',
  /** 적 기병이 크게 는다(`CAVALRY_EXTRA_PCT`). 답: 창. */
  CavalryCorps: 'cavalry',
  /** 적 병사 방어 +2. 답: 관통(도검). */
  HeavyInfantry: 'heavy',
  /** 적 레벨이 낮은 대신 머릿수가 크게 는다. 답: 장수의 휩쓸기·함성, 포위 조심. */
  Horde: 'horde',
  /** 숲이 세 배. 화살이 가리고 기병 돌격이 없다. */
  Forest: 'forest',
  /** 늪이 세 배. 누구나 느리고, 늪에서는 기병이 돌격하지 못한다(`roughGround`). */
  Marsh: 'marsh',
  /** 폭 1.5배(`PLAINS_WIDTH_PCT`), 바위·숲 없음. 기병의 우회·포위가 쉽다 — 적도. */
  Plains: 'plains',
  /** 가운데를 가로지르는 얕은 물줄기(`Cell.Ford`). 느리고 방어 −1, 돌격 없음. 먼저 건너는 쪽이 불리하다. */
  Ford: 'ford',
  /** 적이 언덕 위에 서서 기다린다(`raiseHill`, `World.enemyHold`). 맞거나 때가 지나면 내려온다. */
  HighGround: 'highground',
  /** 말을 탈 수 없다. 양쪽 모두 말에서 내려 걷는다(`dismount`). */
  MountainPath: 'mountain',
  /**
   * 좁은 협곡. 전장 폭 절반(`CANYON_WIDTH_PCT`), 출전은 두 분대까지(`CANYON_SQUADS`, `fieldedSquads`) — 진군할 때
   * 데려갈 분대를 고른다. 적도 다 못 들어온다(`CANYON_POWER_PCT`).
   */
  Canyon: 'canyon',
  /** 순풍. 우리 화살이 멀리, 적 화살이 짧게 난다(`WIND_PCT`). */
  Tailwind: 'tailwind',
  /** 역풍. 적 화살이 멀리, 우리 화살이 짧게 난다. */
  Headwind: 'headwind',
  /** 비. 양쪽 궁수의 사거리가 짧고(`RAIN_RANGE_PCT`) 장전이 느리다(`RAIN_CD_PCT`). 기름 먹인 시위(군수품)면 우리는 그대로. */
  Rain: 'rain',
  /** 매복. 적 `AMBUSH_PCT`%가 더, `AMBUSH_TICKS` 뒤 우리 전열 뒤 양끝에서 나타난다. */
  Ambush: 'ambush',
  /** 원군. `REINFORCE_EVERY`마다 적 `REINFORCE_PCT`%가 온다(`REINFORCE_WAVES`번). 그 전에 끝내면 오지 않는다. */
  Reinforce: 'reinforce',
  /** 수비전. 우리 앞에 거마 두 줄(`plantStakes`), 적은 `SIEGE_POWER_PCT`%로 몰려온다. */
  Siege: 'siege',
  /** 야습. 밤이라 양쪽 궁수의 사거리가 `NIGHT_RANGE_PCT`%, 적은 가까이 와야 보인다(화면). 횃불(군수품)이면 우리는 그대로. */
  Night: 'night',
  /** 성문(관문 전용). 적 앞을 성벽이 가로막고 문(틈) 셋만 열려 있다(`raiseWall`). 적은 성 안에서 기다린다(`enemyHold`). */
  Fortress: 'fortress',
  /** 적장 결전(관문 전용). 적장이 셋이다(`WARLORDS`). 모두 베면 적 전군이 무너진다(`World.warlords`). */
  Warlords: 'warlords',
  /** 정예 소수. 적 레벨 +`ELITE_LEVEL_UP`, 같은 전투력이라 머릿수는 준다. 답: 한곳에 모아 치기, 함성. */
  Elite: 'elite',
  /** 결사대. 적 병사가 무너지지 않는다(`World.diehard`). 답: 오래 버틸 부대, 군량(군수품). */
  Diehard: 'diehard',
  /** 보급 끊김. 우리 궁수의 화살이 `SUPPLY_CUT_PCT`%. 답: 화살 보급(군수품), 근접 편성. */
  SupplyCut: 'supplycut',
  /** 안개. 예보에 이 깊이의 다른 특성이 가려진다(`forecastTraits`), 양쪽 궁수 사거리 `FOG_RANGE_PCT`%. 답: 척후(군수품). */
  Fog: 'fog',
  /** 거센 적(요철). 적이 여느 때보다 많다(`SURGE_POWER_PCT`). 관문 사이 네 판 가운데 한 판(`tideAt`). */
  Surge: 'surge',
  /** 지친 적(요철). 적이 여느 때보다 적다(`LULL_POWER_PCT`). 관문 사이 네 판 가운데 한 판(`tideAt`). */
  Lull: 'lull',
} as const;
export type Trait = (typeof Trait)[keyof typeof Trait];

/** 특성의 갈래. 한 깊이에 같은 갈래는 한 장만 — 숲과 늪이 겹치면 땅이 온통 막힌다. 전황(`Cond`)은 날씨와 흐름이다. */
// `Tide`는 요철(`Trait.Surge`·`Trait.Lull`)의 갈래다. 땅·적·전황의 뽑기(`traitsForDepth`의 `pick`)에는 나오지 않고
// `tideAt`이 따로 정한다 — 다른 특성의 난수 수열을 건드리지 않는다.
export const TraitKind = { Land: 'land', Foe: 'foe', Cond: 'cond', Tide: 'tide' } as const;
export type TraitKind = (typeof TraitKind)[keyof typeof TraitKind];

export interface TraitDef {
  readonly id: Trait;
  readonly kind: TraitKind;
  readonly name: string;
  readonly hanja: string;
  /** 이 깊이부터 나온다. 초반에는 쉬운 것만. */
  readonly minDepth: number;
  /**
   * 이 특성이 붙은 판의 전과 가산(백분율). 어려운 곳을 넘는 것이 이득이 되게. 표준 부대로 잰 어려움에 맞췄다 — 깊이
   * 17·18에서 궁수대(이김 6 → 2/15)·중장보병이 가장 어렵고, 산림·늪지는 거의 같았다. 2단계: 적의 고지가 가장 어렵고
   * (깊이 17 대형 15 → 7/15, 공격 11 → 2/15), 여울이 그다음(대형 15 → 10/15), 넓은 평원·산길은 거의 같았다.
   * 좁은 협곡은 적이 75%라(`CANYON_POWER_PCT`) 처치 격도 그만큼 준다 — 가산이 그것을 메우고도 남아야 넘을 값이 있다
   * (0.75 × 1.5 ≈ 1.13). 처음 10%로 두었을 때는 협곡을 넘을수록 손해였다.
   */
  readonly meritPct: number;
  /** 무엇이 다른가(한 줄). */
  readonly summary: string;
  /** 무엇으로 답하는가(한 줄). 결과 화면의 예보가 적는다. */
  readonly hint: string;
  /** 관문 이야기 판에만 붙는다(`GATE_TRAITS`) — 여느 깊이의 뽑기(`traitsForDepth`)에는 나오지 않는다. */
  readonly gateOnly?: boolean;
}

export const TRAITS: readonly TraitDef[] = [
  {
    id: Trait.Forest, kind: TraitKind.Land, name: '산림', hanja: '山林', minDepth: 4, meritPct: 5,
    summary: '숲이 우거졌다. 화살이 가리고 기병은 돌격하지 못한다.',
    hint: '칼과 창의 판이다.',
  },
  {
    id: Trait.Marsh, kind: TraitKind.Land, name: '늪지', hanja: '沼澤', minDepth: 4, meritPct: 5,
    summary: '늪이 넓다. 누구나 느려지고 늪에서는 기병이 돌격하지 못한다.',
    hint: '기병을 믿지 마라. 궁수가 오래 쏜다.',
  },
  {
    id: Trait.Plains, kind: TraitKind.Land, name: '넓은 평원', hanja: '平原', minDepth: 4, meritPct: 5,
    summary: '전장이 넓고 바위도 숲도 없다. 돌아 들어가기 쉽다 — 적도.',
    hint: '기병으로 옆을 쳐라. 궁수를 옆에 홀로 두지 마라.',
  },
  {
    id: Trait.Ford, kind: TraitKind.Land, name: '여울', hanja: '渡河', minDepth: 6, meritPct: 10,
    summary: '가운데를 얕은 물이 가로지른다. 물에 선 사람은 느리고 방어가 깎인다.',
    hint: '먼저 건너지 마라. 대기로 버티고 궁수로 건너는 적을 쏴라.',
  },
  {
    id: Trait.HighGround, kind: TraitKind.Land, name: '적의 고지', hanja: '高地', minDepth: 8, meritPct: 15,
    summary: '적이 언덕 위에서 기다린다. 위에서 치는 쪽이 더 아프다.',
    hint: '정면으로 오르지 마라. 대기로 버티면 내려온다 — 궁수로 끌어내리거나 옆으로 돌아 올라라.',
  },
  {
    id: Trait.MountainPath, kind: TraitKind.Land, name: '산길', hanja: '山路', minDepth: 10, meritPct: 5,
    summary: '말을 탈 수 없다. 기병과 장수 모두 말에서 내려 걷는다 — 적도.',
    hint: '기병에 기대는 부대라면 조심하라. 창과 칼의 판이다.',
  },
  {
    id: Trait.Canyon, kind: TraitKind.Land, name: '좁은 협곡', hanja: '隘路', minDepth: 8, meritPct: 50,
    summary: '길이 좁아 두 분대만 들어간다. 돌아 들어갈 틈도 없다.',
    hint: '근접 두 분대로 길목을 막아라. 좁은 길에서는 궁수가 쏠 자리가 없고, 앞을 한 분대로 막으면 장수가 둘러싸인다.',
  },
  {
    id: Trait.ArcherCorps, kind: TraitKind.Foe, name: '궁수대', hanja: '弓手隊', minDepth: 6, meritPct: 15,
    summary: '적 궁수가 두 배다.',
    hint: '기병으로 궁수를 덮치고, 창으로 궁수 앞을 막아라.',
  },
  {
    id: Trait.CavalryCorps, kind: TraitKind.Foe, name: '기마대', hanja: '騎馬隊', minDepth: 8, meritPct: 15,
    summary: '적 기병이 크게 늘었다.',
    hint: '창을 앞에 세워라. 궁수를 홀로 두지 마라.',
  },
  {
    id: Trait.HeavyInfantry, kind: TraitKind.Foe, name: '중장보병', hanja: '重裝步兵', minDepth: 10, meritPct: 15,
    summary: '적 병사가 두꺼운 갑옷을 입었다(방어 +2).',
    hint: '갑옷을 깨는 도검이 값을 한다.',
  },
  {
    id: Trait.Horde, kind: TraitKind.Foe, name: '인해', hanja: '人海', minDepth: 12, meritPct: 10,
    summary: '적이 약한 대신 머릿수가 크게 많다.',
    hint: '둘러싸이지 마라. 장수의 휩쓸기와 함성이 값을 한다.',
  },
  // 관문 전용(5단계). 뒤에 붙인다 — 앞의 순서를 흔들지 않는다.
  {
    id: Trait.Fortress, kind: TraitKind.Land, name: '성문', hanja: '城門', minDepth: 10, meritPct: 10, gateOnly: true,
    summary: '적 앞을 성벽이 가로막고 문 셋만 열려 있다. 적은 성 안에서 기다린다.',
    hint: '문으로 몰려 들어가라 — 창을 앞세우고, 궁수는 문 너머를 쏘게.',
  },
  {
    id: Trait.Warlords, kind: TraitKind.Foe, name: '적장 결전', hanja: '決戰', minDepth: 20, meritPct: 10, gateOnly: true,
    summary: '적장이 셋이다. 셋을 모두 베면 적 전군이 무너진다.',
    hint: '적장을 노려라. 기병과 장수의 휩쓸기로 적장에게 닿아라.',
  },
  // 전황(3단계). 뒤에 붙인다 — `sanitizeTraits`가 이 순서로 고정하므로 앞의 순서를 흔들지 않는다.
  {
    id: Trait.Tailwind, kind: TraitKind.Cond, name: '순풍', hanja: '順風', minDepth: 6, meritPct: 0,
    summary: '바람이 우리 등 뒤에서 분다. 우리 화살은 멀리, 적 화살은 짧게 난다.',
    hint: '궁수가 먼저 쏜다. 서두르지 말고 쏘게 두라.',
  },
  {
    id: Trait.Headwind, kind: TraitKind.Cond, name: '역풍', hanja: '逆風', minDepth: 6, meritPct: 5,
    summary: '바람이 적 쪽에서 분다. 적 화살은 멀리, 우리 화살은 짧게 난다.',
    hint: '궁수로 버티지 마라. 창·칼로 빨리 붙어라.',
  },
  {
    id: Trait.Rain, kind: TraitKind.Cond, name: '비', hanja: '雨', minDepth: 8, meritPct: 5,
    summary: '비에 활시위가 늘어진다. 양쪽 궁수의 사거리가 짧고 장전이 느리다.',
    hint: '근접 편성으로. 기름 먹인 시위(군수품)가 있으면 우리 궁수는 그대로다.',
  },
  {
    id: Trait.Ambush, kind: TraitKind.Cond, name: '매복', hanja: '伏兵', minDepth: 12, meritPct: 15,
    summary: '10초 뒤 적 한 무리가 우리 뒤 양끝에서 나타난다.',
    hint: '궁수를 뒤에 홀로 두지 마라. 한 분대는 뒤를 지켜라.',
  },
  {
    id: Trait.Siege, kind: TraitKind.Cond, name: '수비전', hanja: '守城', minDepth: 10, meritPct: 15,
    summary: '우리 앞에 거마가 두 줄 서 있고, 적이 더 많이 몰려온다.',
    hint: '거마 뒤에서 대기로 받아라. 말은 말뚝에 걸리고 궁수가 쏠 틈이 난다.',
  },
  {
    id: Trait.Night, kind: TraitKind.Cond, name: '야습', hanja: '夜襲', minDepth: 16, meritPct: 10,
    summary: '밤이다. 양쪽 궁수가 멀리 못 쏘고, 적은 가까이 와야 보인다.',
    hint: '대형을 지키고 함성으로 버텨라. 횃불(군수품)이 있으면 우리 궁수는 그대로다.',
  },
  {
    id: Trait.Reinforce, kind: TraitKind.Cond, name: '원군', hanja: '援軍', minDepth: 14, meritPct: 15,
    summary: '20초마다 적 원군이 온다(두 번). 그 전에 끝내면 오지 않는다.',
    hint: '빨리 끝내라. 기병과 돌격으로 몰아쳐라.',
  },
  // Todo5에서 미룬 것(7단계). 뒤에 붙인다 — `sanitizeTraits`가 이 순서로 고정하므로 앞의 순서를 흔들지 않는다.
  {
    id: Trait.Elite, kind: TraitKind.Foe, name: '정예 소수', hanja: '精銳', minDepth: 12, meritPct: 15,
    summary: '적이 적지만 한 사람 한 사람이 세다.',
    hint: '흩어져 맞서지 말고 한곳에 모아 쳐라. 포위가 가장 잘 먹힌다.',
  },
  {
    id: Trait.Diehard, kind: TraitKind.Foe, name: '결사대', hanja: '決死', minDepth: 16, meritPct: 10,
    summary: '적이 무너지지 않는다. 마지막 한 명까지 싸운다.',
    hint: '적장을 베어도 흔들리지 않는다. 오래 버틸 부대로, 군량(군수품)을 챙겨라.',
  },
  {
    id: Trait.SupplyCut, kind: TraitKind.Cond, name: '보급 끊김', hanja: '斷糧', minDepth: 10, meritPct: 10,
    summary: '우리 궁수의 화살이 셋에 하나도 안 된다.',
    hint: '화살 보급(군수품)을 챙기거나 칼과 창으로 싸워라.',
  },
  {
    id: Trait.Fog, kind: TraitKind.Cond, name: '안개', hanja: '霧', minDepth: 8, meritPct: 5,
    summary: '앞이 보이지 않는다. 무엇이 기다리는지 미리 알 수 없고, 양쪽 궁수가 멀리 못 쏜다.',
    hint: '척후(군수품)를 보내면 앞의 세 깊이를 안개 없이 본다.',
  },
  // 요철(밸런스 2). 뒤에 붙인다 — 앞의 순서를 흔들지 않는다.
  {
    id: Trait.Surge, kind: TraitKind.Tide, name: '거센 적', hanja: '強敵', minDepth: 6, meritPct: 10,
    summary: '적의 기세가 올랐다. 여느 때보다 수가 많다.',
    hint: '이 구간에서 가장 힘든 판이다. 병사가 줄었으면 여기서 돌아가는 것도 수다.',
  },
  {
    id: Trait.Lull, kind: TraitKind.Tide, name: '지친 적', hanja: '疲敵', minDepth: 6, meritPct: 0,
    summary: '적이 지쳐 있다. 여느 때보다 수가 적다.',
    hint: '쉬어 가는 판이다. 병사를 아끼며 넘어라.',
  },
];

export const TRAIT_BY_ID: ReadonlyMap<string, TraitDef> = new Map(TRAITS.map((t) => [t.id, t]));

/** 정예 소수: 적 레벨 가산. 목표 전투력은 그대로라 머릿수가 준다(인해의 거꾸로). */
export const ELITE_LEVEL_UP = 18;
/** 안개: 양쪽 궁수의 사거리 백분율. */
export const FOG_RANGE_PCT = 85;

/**
 * 예보에 보이는 특성. 안개(`Trait.Fog`)가 낀 깊이는 척후를 보내지 않았으면 안개만 보인다 — 무엇이 기다리는지 모르고
 * 들어가는 것이 안개의 뜻이다. 판에 들어서면(전투 준비·전장 칩) 다 보인다.
 */
export function forecastTraits(traits: readonly Trait[], scouted: boolean): Trait[] {
  return !scouted && traits.includes(Trait.Fog) ? [Trait.Fog] : [...traits];
}

/** 보급 끊김: 우리 궁수 화살의 백분율(화살 보급은 그 뒤에 더한다). */
export const SUPPLY_CUT_PCT = 30;

/** 중장보병의 적 병사 방어 가산. */
export const HEAVY_DEF_BONUS = 2;
/**
 * **요철**(밸런스 2): 관문 사이 네 판 가운데 한 판은 적이 `SURGE_POWER_PCT`%로 튀고(거센 적), 다른 한 판은
 * `LULL_POWER_PCT`%로 쉬어 간다(지친 적). 사용자가 청했다: "난이도 계단이 중간중간 살짝 튀어서 어려워졌다가 살짝 쉬워지는
 * 구간도 넣어주면 좀 다이나믹할 거 같아." 적 곡선은 관문 사이에서 깊이마다 같은 비율로만 오른다 — 네 판이 다 비슷했다.
 * 자리는 출정 시드가 정하고(`tideAt`) 결과 화면의 예보에 다른 특성처럼 보인다. 첫 관문 앞(깊이 1~4)과 관문에는 없다.
 * 영토전은 그 땅의 특성으로 싸우므로 없다. 두 판을 더하면 +5%라 구간의 평균은 거의 그대로다.
 */
export const SURGE_POWER_PCT = 115;
export const LULL_POWER_PCT = 90;

/** 인해: 적 레벨을 이만큼 낮추고(1 아래로는 안 간다) 목표 전투력은 `HORDE_POWER_PCT`%로. 머릿수가 그만큼 는다. */
export const HORDE_LEVEL_DROP = 15;
export const HORDE_POWER_PCT = 110;
/**
 * 기마대: 편성 비중 합의 이만큼(백분율)을 기병에 더 얹고 목표 전투력을 `CAVALRY_POWER_PCT`%로. 처음에는 30%만 얹고
 * 전투력은 그대로 두었더니, 기병이 는 만큼 다른 병종이 줄어 창을 뺀 부대도 거의 어렵지 않았다(깊이 16·17 표준에서
 * 칼로 창을 바꿔도 이긴 판이 같았다). 대비를 요구하는 특성이 되려면 더 세야 한다.
 *
 * 목표 전투력은 **110%다(115였다 — REPLAY 120).** 플레이한 사람이 정했다(2026-10-07): "관문 5는 기병 +10프로만하자" —
 * 첫 관문(적 선봉)이 이 특성의 판이고, 관문 5는 앞 깊이의 2.14배로 가장 크게 튀는 관문이었다. 특성의 값이라 기마대가
 * 붙는 다른 판(되풀이 관문 35·65, 길에서 뽑힌 판)도 함께 110%가 된다.
 */
export const CAVALRY_EXTRA_PCT = 60;
export const CAVALRY_POWER_PCT = 110;
/** 산림·늪지: 그 지형 덩어리 수를 이 배수로. */
export const LAND_MULT = 3;
/** 넓은 평원: 전장 폭(칸 수)을 이 백분율로. */
export const PLAINS_WIDTH_PCT = 150;
/**
 * 적의 고지: 적이 언덕에서 기다리는 최대 틱(30틱 = 1초). 그 전에 적 하나라도 맞으면 모두 내려온다. 끝없이 기다리면
 * 대기만 걸어 두는 플레이어와 서로 쳐다보다 시간이 끝난다.
 */
export const HIGH_GROUND_HOLD_TICKS = 30 * 30;
/** 좁은 협곡: 전장 폭(칸 수)을 이 백분율로. */
export const CANYON_WIDTH_PCT = 50;
/**
 * 좁은 협곡: 출전하는 분대 수의 상한. 장수는 늘 나간다 — 직할대 병사(0번 분대)도 한 분대로 센다. 장수 곁의 동료
 * (부관, `escortSquad`)는 장수와 함께 서므로 세지 않는다.
 */
export const CANYON_SQUADS = 2;
/**
 * 좁은 협곡: 적의 목표 전투력을 이 백분율로. 길이 좁은 것은 적도 같다 — 우리만 두 분대로 줄면 네 분대를 채운 부대가
 * 반쪽으로 온전한 적을 맞는다.
 */
export const CANYON_POWER_PCT = 75;
/** 순풍·역풍: 바람을 등진 쪽의 화살 사거리 +이만큼(백분율), 맞바람 쪽 −이만큼. */
export const WIND_PCT = 20;
/** 야습: 양쪽 궁수의 사거리 백분율. */
export const NIGHT_RANGE_PCT = 60;
/** 비: 양쪽 궁수의 사거리·장전 간격 백분율. */
export const RAIN_RANGE_PCT = 75;
export const RAIN_CD_PCT = 125;
/**
 * 매복: 더 오는 적의 몫(처음 편성의 백분율)과 나타나는 틱(30틱 = 1초). 처음에는 본대에서 떼어 우리 옆에 세웠는데,
 * 그러면 나뉜 적을 따로 잡아 오히려 쉬웠다(깊이 18 이김 7 → 10/15). 본대는 그대로 두고 더 보내며, 우리 전열 뒤에서
 * 나타나 뒤에 선 궁수를 노린다.
 */
export const AMBUSH_PCT = 30;
export const AMBUSH_TICKS = 10 * 30;
/** 원군: 오는 횟수, 간격(틱), 한 번에 오는 몫(처음 편성의 백분율). 세 번이면 깊이 18 이김이 7 → 0/15로 너무 셌다. */
export const REINFORCE_WAVES = 2;
export const REINFORCE_EVERY = 20 * 30;
export const REINFORCE_PCT = 20;
/**
 * 수비전: 적의 목표 전투력 백분율. 거마는 말만 막으므로 125%로는 그냥 "적이 25% 많은 판"이 되어 깊이 17 이김이 15 →
 * 6/15, 18이 0/15였다(대기로 받아도 같았다).
 */
export const SIEGE_POWER_PCT = 110;

/**
 * 좁은 협곡에서 전장에 설 분대. 병사가 있는 분대와 동료가 맡은 분대(부관 자리는 빼고)를 오름차순으로 모아
 * `CANYON_SQUADS`까지. 명부가 고른 대로 싣지만(`buildConfig`), 리플레이는 아무 명부나 실어 오므로 전투를 만드는 쪽
 * (`createBattle`)이 이것으로 한 번 더 자른다 — 넷을 실어 온 명부도 앞의 둘만 선다.
 */
export function fieldedSquads(
  roster: readonly { readonly count: number; readonly squad?: number }[],
  companions: readonly (string | null)[] | undefined,
  escortSquad: number,
): number[] {
  const used = new Set<number>();
  for (const e of roster) if (e.count > 0) used.add(e.squad ?? 0);
  (companions ?? []).forEach((c, sq) => {
    if (c && sq !== escortSquad) used.add(sq);
  });
  return [...used].sort((a, b) => a - b).slice(0, CANYON_SQUADS);
}

/** 한 판에 붙는 특성의 상한. 관문 이야기 판(`GATE_TRAITS`)이 가장 많이 붙인다. */
export const MAX_TRAITS = 4;

/**
 * 믿을 수 없는 특성 목록을 거른다. 모르는 것·겹친 것은 버리고, 순서는 `TRAITS` 순서로 고정하고, `MAX_TRAITS`까지 —
 * 리플레이 JSON은 아무 목록이나 실어 온다. 순서를 고정하지 않으면 같은 특성인데 적용 순서가 갈릴 수 있다.
 *
 * 예전에는 같은 갈래 둘째 장도 버렸다. 관문 이야기 판(5단계)이 야습·매복(둘 다 전황)처럼 한 갈래 둘을 붙이게 되어
 * 뺐다 — 여느 깊이의 뽑기(`traitsForDepth`)는 여전히 갈래마다 한 장이고, 검증(`verifyReplay`)이 출정 시드·깊이에서 다시
 * 낸 목록(`traitsOfBattle`)과 그대로 맞춰 보므로 다른 조합을 실은 리플레이는 거기서 거절된다.
 */
export function sanitizeTraits(raw: readonly unknown[] | null | undefined): Trait[] {
  if (!Array.isArray(raw)) return [];
  const want = new Set(raw.filter((x): x is string => typeof x === 'string'));
  return TRAITS.filter((t) => want.has(t.id))
    .map((t) => t.id)
    .slice(0, MAX_TRAITS);
}

/**
 * **관문 이야기 판**(Todo5 5단계). 관문(5·10·…)은 추세보다 크게 튀는 판이다(`GATE_PCT`) — 여기에 깊이마다 정해진 한 판의
 * 이야기를 준다. 출정 시드와 무관하게 늘 같다(관문은 외워서 준비하는 판이다).
 */
export const GATE_STORIES: Readonly<Record<number, { readonly name: string; readonly traits: readonly Trait[] }>> = {
  5: { name: '적 선봉', traits: [Trait.CavalryCorps] },
  10: { name: '성문 공방', traits: [Trait.Fortress, Trait.ArcherCorps] },
  15: { name: '강 건너기', traits: [Trait.Ford, Trait.ArcherCorps] },
  20: { name: '적장 결전', traits: [Trait.Warlords, Trait.HeavyInfantry] },
  25: { name: '야습', traits: [Trait.Night, Trait.Ambush] },
  30: { name: '왕의 친위대', traits: [Trait.HighGround, Trait.HeavyInfantry, Trait.Warlords] },
};

/** 이야기가 적힌 마지막 관문. */
const LAST_STORY_GATE = 30;

/**
 * 그 관문의 이야기. 깊이가 무한이 되며(Todo9) 30 너머의 관문(35·40·…)은 여섯 이야기를 차례로 되풀이한다(35 = 선봉,
 * 40 = 성문 …) — 적은 레벨이 올라 더 세다(`depthRoster`). 처음에는 표에 없는 관문이 이야기도 특성도 없는 빈 판이었고, 결과
 * 화면이 "관문 — "로 끝났다. 관문이 아니면 null.
 */
export function gateStory(depth: number): { readonly name: string; readonly traits: readonly Trait[] } | null {
  const d = Math.floor(depth);
  if (d <= 0 || d % 5 !== 0) return null;
  const at = d <= LAST_STORY_GATE ? d : ((d / 5 - 1) % (LAST_STORY_GATE / 5)) * 5 + 5;
  return GATE_STORIES[at] ?? null;
}

/** 관문 이야기의 이름. 관문이 아니면 null. */
export function gateStoryName(depth: number): string | null {
  return gateStory(depth)?.name ?? null;
}

/** 성문: 적이 성 안에서 기다리는 최대 틱. 적의 고지와 같다 — 맞으면 모두 나온다. */
export const FORTRESS_HOLD_TICKS = 30 * 30;
/** 적장 결전: 적장 수. */
export const WARLORDS = 3;

export function hasTrait(traits: readonly Trait[] | undefined, t: Trait): boolean {
  return traits !== undefined && traits.includes(t);
}

/** 특성들의 이름(받은 순서 그대로, 모르는 것만 뺀다 — 거른 목록을 넘길 것). 화면이 모두 이것으로 적는다. */
export function traitNames(traits: readonly Trait[] | undefined): string[] {
  if (!traits) return [];
  return traits.map((t) => TRAIT_BY_ID.get(t)?.name).filter((n): n is string => n !== undefined);
}

/**
 * 전장 칩(장수 체력 막대 안)에 적는 짧은 글. 둘까지는 이름을 늘어놓고, 셋부터는 "거센 적 외 2"로 줄인다. 요철
 * (`TraitKind.Tide`)이 붙으며 관문이 아닌 판도 특성이 셋이 될 수 있게 됐고, 폭 360에서 셋을 늘어놓은 글이 체력 숫자와
 * 겹쳤다(QA가 짚었다). 요철이 있으면 그 이름을 남긴다 — 그 판의 세기를 말하는 장이다. 전체 목록은 칩의 `title`에 있다.
 */
export function traitChip(traits: readonly Trait[] | undefined): string {
  const names = traitNames(traits);
  if (names.length < 3) return names.join(' · ');
  const tide = (traits ?? []).map((t) => TRAIT_BY_ID.get(t)).find((t) => t?.kind === TraitKind.Tide)?.name;
  return `${tide ?? names[0]} 외 ${names.length - 1}`;
}

/**
 * 판의 시드 = 출정 시드 + 깊이 × `BATTLE_SEED_STEP`. 한 곳에만 적는다 — 명부를 만드는 쪽(`buildConfig`)과 검증
 * (`traitsOfBattle`)이 따로 적으면 한쪽만 고쳐졌을 때 정직한 리플레이가 거절된다(리뷰가 짚었다).
 */
export const BATTLE_SEED_STEP = 1013;

export function battleSeed(runSeed: number, depth: number): number {
  return runSeed + depth * BATTLE_SEED_STEP;
}

/**
 * 판의 시드와 깊이에서 그 판에 붙었어야 할 특성. 판 시드에서 출정 시드를 되짚어 `traitsForDepth`를 부른다 — 서버가
 * 명부 없이 다시 낼 수 있다. 처음에는 명부의 특성을 거르기만 해서, 관문 판에 늪지·인해를 실은 리플레이가 전과를
 * 부풀린 채 통과했다(리뷰가 짚었다).
 */
export function traitsOfBattle(seed: number, depth: number, route: Route | null): Trait[] {
  return traitsForDepth(seed - depth * BATTLE_SEED_STEP, depth, route);
}

/**
 * **갈림길**(Todo5 6단계). 관문을 넘으면 다음 네 깊이(관문 5를 넘으면 6–9)를 어느 길로 갈지 고른다 — 길은 그 구간의
 * 땅을 정한다. 한 번 고르면 네 판을 버텨야 하므로 편성에 맞는 길을 고르는 것이 이 선택의 뜻이다(보병 부대는 산골,
 * 기병 부대는 들). 출정을 중간 깊이(6·11·…)에서 시작해도 그 자리에서 고른다.
 *
 * 플레이한 사람이 골랐다: "관문마다 구간 길", "땅으로 가른다". 적 편성·전황은 길과 무관하게 뽑는다 — 다만 들길은
 * 적 기마대가 잦다(말이 달리기 좋은 땅이다). 전과 가산은 붙은 특성을 따른다(길 자체에는 없다).
 */
export const Route = {
  /** 산골 길: 협곡·산림·산길·늪지 — 말이 힘든 땅. */
  Hills: 'hills',
  /** 들길: 넓은 평원·여울·적의 고지, 적 기마대가 잦다. */
  Fields: 'fields',
} as const;
export type Route = (typeof Route)[keyof typeof Route];

export interface RouteDef {
  readonly id: Route;
  readonly name: string;
  readonly hanja: string;
  /** 어떤 땅인가(한 줄). */
  readonly summary: string;
  /** 누구의 길인가(한 줄). */
  readonly hint: string;
  /** 이 길에서 뽑는 땅. 나머지 땅은 이 길에 나오지 않는다. */
  readonly lands: readonly Trait[];
}

export const ROUTES: readonly RouteDef[] = [
  {
    id: Route.Hills, name: '산골 길', hanja: '山谷',
    summary: '산림·늪지·좁은 협곡·산길 — 말이 힘든 땅이다.',
    hint: '칼과 창, 걷는 부대의 길.',
    lands: [Trait.Forest, Trait.Marsh, Trait.Canyon, Trait.MountainPath],
  },
  {
    id: Route.Fields, name: '들길', hanja: '平野',
    summary: '넓은 평원·여울·적의 고지 — 적 기마대가 잦다.',
    hint: '기병과 창, 넓게 도는 부대의 길.',
    lands: [Trait.Plains, Trait.Ford, Trait.HighGround],
  },
];
export const ROUTE_BY_ID: ReadonlyMap<string, RouteDef> = new Map(ROUTES.map((r) => [r.id, r]));

/** 들길에서 적 기마대가 뽑기 풀에 더 들어가는 장 수(원래 한 장에 더해). */
export const FIELDS_CAVALRY_EXTRA = 1;

/** 길을 고른 구간의 깊이인가 — 첫 관문 뒤, 관문이 아닌 깊이. */
export function isRouteDepth(depth: number): boolean {
  const d = Math.floor(depth);
  return d > 5 && d % 5 !== 0;
}

/** 깊이가 속한 구간(관문 사이). 6–9 → 1, 11–14 → 2 … 같은 구간은 같은 길을 간다. */
export function routeSegment(depth: number): number {
  return Math.floor(Math.floor(depth) / 5);
}

/** 그 구간의 깊이들(관문 뺀 넷). */
export function segmentDepths(depth: number): number[] {
  const base = routeSegment(depth) * 5;
  return [base + 1, base + 2, base + 3, base + 4];
}

/** 믿을 수 없는 길 값을 거른다. 모르면 null. */
export function sanitizeRoute(raw: unknown): Route | null {
  return typeof raw === 'string' && ROUTE_BY_ID.has(raw) ? (raw as Route) : null;
}

/** 특성들의 전과 가산 합(백분율). */
export function traitMeritPct(traits: readonly Trait[] | undefined): number {
  if (!traits) return 0;
  let n = 0;
  for (const t of traits) n += TRAIT_BY_ID.get(t)?.meritPct ?? 0;
  return n;
}

/**
 * 출정 시드와 깊이가 정하는 그 깊이의 특성. 같은 출정이면 늘 같다 — 결과 화면이 다음 깊이를 예보할 수 있다.
 *
 * 곡선: 깊이 1–3 없음(배우는 구간), 4 땅 하나, 관문(5·10·…)은 정해진 이야기 판(`GATE_STORIES`),
 * 6–15 하나(반은 길의 땅, 반은 적이나 전황), 16–25 길의 땅 + 절반의 판에 적이나 전황 하나, 26–29 길의 땅 + 적이나
 * 전황 하나. 6단계 전에는 세 갈래(땅·적·전황)를 고르게 뽑았다. 고를 수 있는 것은 그 깊이에 열린 것(`minDepth`)만.
 * 난수는 늘 같은 횟수를 굴린다 — 고를 것이 없어도, 길이 달라도.
 *
 * `route`: 그 구간에 고른 길(`Route`). 갈림길 구간(`isRouteDepth`)에서 null이면 빈 목록 — 길을 모르면 특성도 모른다.
 * 다른 깊이에서는 쓰지 않는다.
 */
export function traitsForDepth(runSeed: number, depth: number, route: Route | null): Trait[] {
  const d = Math.floor(depth);
  // 관문은 정해진 이야기 판이다(`GATE_STORIES`). 난수를 굴리지 않는다.
  if (d % 5 === 0) return sanitizeTraits(gateStory(d)?.traits ?? []);
  if (d <= 3) return [];
  // 갈림길 구간인데 길을 모르면 특성도 모른다 — 화면은 "갈림길"로 적고, 판은 길을 고른 뒤에야 연다.
  const way = isRouteDepth(d) ? (route === null ? null : ROUTE_BY_ID.get(route) ?? null) : null;
  if (isRouteDepth(d) && way === null) return [];
  const rng = new Rng((runSeed ^ Math.imul(d, 0x9e3779b1)) >>> 0);
  const pick = (kind: TraitKind): Trait | null => {
    let pool = TRAITS.filter((t) => t.kind === kind && t.minDepth <= d && !t.gateOnly).map((t) => t.id);
    // 길이 땅을 정한다. 들길은 적 기마대를 한 장 더 넣는다(열린 깊이에서만).
    if (way && kind === TraitKind.Land) pool = pool.filter((t) => way.lands.includes(t));
    if (way?.id === Route.Fields && kind === TraitKind.Foe && pool.includes(Trait.CavalryCorps)) {
      for (let k = 0; k < FIELDS_CAVALRY_EXTRA; k++) pool.push(Trait.CavalryCorps);
    }
    const k = rng.int(pool.length > 0 ? pool.length : 1);
    return pool.length > 0 ? pool[k] : null;
  };
  const r1 = rng.int(100);
  const r2 = rng.int(2);
  const second = rng.int(100);
  const land = pick(TraitKind.Land);
  const byKind = new Map<TraitKind, Trait | null>([
    [TraitKind.Foe, pick(TraitKind.Foe)],
    [TraitKind.Cond, pick(TraitKind.Cond)],
  ]);
  // 땅이 아닌 갈래 가운데 열린 것 하나(정해진 순서).
  const open = [TraitKind.Foe, TraitKind.Cond].filter((k) => byKind.get(k) !== null);
  const other = open.length > 0 ? byKind.get(open[r2 % open.length])! : null;
  const out: Trait[] = [];
  if (d === 4) {
    if (land) out.push(land);
  } else if (d <= 15) {
    // 한 장: 반은 길의 땅, 반은 적이나 전황. 처음(2단계까지)에는 세 갈래를 고르게 뽑았다 — 길이 생기며 땅을 반으로
    // 올렸다. 땅이 셋에 하나면 고른 길이 네 판 가운데 하나에서만 보였다.
    const one = r1 < 50 ? land : (other ?? land);
    if (one) out.push(one);
  } else {
    // 16–25는 길의 땅에 반쯤 한 장 더, 26–29는 늘 둘. 땅은 늘 붙는다 — 뒤로 갈수록 고른 길이 판마다 보인다.
    if (land) out.push(land);
    if (other && (d > 25 || second < 50)) out.push(other);
  }
  // 요철: 땅·적·전황과 따로 정한다(다른 난수). 길을 모르면 위에서 이미 빈 목록으로 돌아갔다.
  const tide = tideAt(runSeed, d);
  if (tide) out.push(tide);
  return sanitizeTraits(out);
}

/**
 * 그 깊이의 요철(`Trait.Surge`·`Trait.Lull`). 없으면 null. 첫 관문 뒤의 구간(6~9, 11~14 …)마다 네 판 가운데 하나가 거센
 * 적, 다른 하나가 지친 적이다. 출정 시드와 구간으로 정한다 — `traitsForDepth`의 난수(`rng`)와 따로 굴려, 요철을 넣었다고
 * 같은 출정의 땅·적·전황이 바뀌지 않는다.
 */
export function tideAt(runSeed: number, depth: number): Trait | null {
  const d = Math.floor(depth);
  if (!isRouteDepth(d)) return null;
  const rng = new Rng((runSeed ^ Math.imul(routeSegment(d) + 1, 0x85ebca6b)) >>> 0);
  const hard = rng.int(4);
  // 지친 적은 거센 적과 다른 자리: 남은 셋 가운데 하나.
  const easy = (hard + 1 + rng.int(3)) % 4;
  const at = (d % 5) - 1;
  return at === hard ? Trait.Surge : at === easy ? Trait.Lull : null;
}
